import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { Composer, type ComposerHandlers } from "./composer.js";
import type { WaitPhase } from "../app/waiting.js";

/**
 * The chat screen's regions (UX spec v3 §6.2, mock-up 02-chat.html): a top bar
 * ("Assistant", back to Home), the stream (`role="log"`) and the composer dock.
 *
 * The assistant's turns are text on the page with the apricot mark; the
 * customer's words are a surface-2 bubble; bank cards are appended by the
 * controller. Interim words live in a bubble that is a button (DCTA-141: a tap
 * opens them in the field for editing), named by what it shows; while the
 * customer speaks the log is quiet (`quiet`) — a screen reader hears the
 * finalised bubble, which is a new node, not every word, and nothing it says
 * can reach the microphone.
 */
export interface ChipOption {
  readonly label: string;
  /** DCTA-143: the app's marker on the chip's second line ("New payee · never paid"), never part of the label. */
  readonly mark?: string | undefined;
  readonly onSelect: () => void;
}

/** The wait for the bank's answer, drawn (DCTA-111, `app/waiting.ts`). */
export interface WorkingRow {
  readonly element: HTMLElement;
  phase(phase: WaitPhase): void;
}

/**
 * What the wait says, phase by phase — the customer's side of it only: no stage, no model, no
 * figure. "The card" is the one that ends the wait. (Accepted in review, DCTA-118 R28; spec §F.2
 * rows 3a–3c. DCTA-217, Umakhan 13 Oct: the second sentence of 3c and 3g replaces the slogan
 * "Nothing moves until you check and sign", which is out of the app.) The quiet phase says it to a
 * screen reader only (`QUIET`): the dots are what a sighted customer sees.
 */
export const WAIT_LINES: Readonly<Record<WaitPhase, string>> = {
  quiet: "",
  slow: "Working on it — this can take a few seconds.",
  still: "Still working on it. You'll check the card before anything is sent.",
  // §F.2 3g (DCTA-130; built in DCTA-131, F9): from 25 s, in the same slot — announced once.
  late: "Taking longer than usual. You'll check the card before anything is sent.",
};

/** §F.2 3a: the row's one status while quiet, visually hidden. */
const QUIET = "Assistant is replying";

/**
 * DCTA-221 (the coordinator's rule, 14 Oct: "a question asked again is shown again, marked as still open"): the mark above
 * a question the customer has just seen on the card and has not answered. Drafted by the build with design:ux-copy from the
 * coordinator's own words — no spec row yet; flagged for ux-designer. Text only, read with the question (the log's turn).
 */
export const STILL_OPEN = "Still open";

export interface QuestionHandle {
  /** The group is answered: `pressed` is the chip that answered it, if a chip did. */
  spend(pressed?: number): void;
  /** The question is asked again in other words: these chips go. */
  withdraw(): void;
  /** DCTA-136: focus is on one of these chips (a keyboard or a screen reader answered with it). */
  holdsFocus(): boolean;
  /** DCTA-136: focus the first chip — the question asked again takes over from a chip that held focus. False: no chips. */
  focus(): boolean;
  /** DCTA-139 (F13, §F.2 16g): the id of the question's sentence — the field's description while it waits for words. */
  readonly describedBy: string;
}

/**
 * DCTA-226 (§5.7): the bank's offer a chip answers by id — its draft and question. Words that ask the bank something while
 * the offer is open go there (`POST /v1/drafts/{draftId}/answer {question_id, words}`); the chip still answers by its choice.
 */
export interface OfferTarget {
  readonly draftId: string;
  readonly questionId: string;
}

/** DCTA-226: each open offer group's target, when its chips answer one offer of the bank's (never in the DOM). */
const offerTargets = new WeakMap<HTMLElement, OfferTarget>();

/** DCTA-139 (F15, F14): the newest offer still open — chips used once — and what words can do to it. */
export interface OpenOffer {
  readonly labels: readonly string[];
  /** DCTA-226: the bank's offer these chips answer — none for an offer of the app's own, or chips of two offers. */
  readonly asks?: OfferTarget;
  /** Asked about once already (16h): the same again lets it go (16i). */
  readonly reasked: boolean;
  reask(): void;
  /** As a tap is: the chip's own action, by its id. */
  pick(index: number): void;
  /** DCTA-226: held while the bank answers words asked at it — a tap neither acts nor spends the group; released, live again. */
  hold(on: boolean): void;
}

/** Ids for the questions' sentences, unique in the page. */
let questionSeq = 0;

const MONEY = /S\$[\d,]+\.\d{2}/;

/** "S$15.00 to SARAH TAN" with the figure in the money face — split as text, never parsed as markup. */
function withMoney(text: string): (Node | string)[] {
  const m = MONEY.exec(text);
  if (m === null) return [text];
  return [text.slice(0, m.index), el("span", { class: "money" }, [m[0]]), text.slice(m.index + m[0].length)];
}

/** Ids for the questions' sentences, unique in the page (the field's description, a group's name). */
function nextQuestionId(): string {
  questionSeq += 1;
  return `q-${String(questionSeq)}`;
}

/**
 * One question's chips and the handle that answers, spends or withdraws them (04; DCTA-189: one per question on a card).
 * A chip is used once: `spend(i)` keeps the chosen one pressed and makes the group inert (the transcript still reads as a
 * conversation); `withdraw()` removes the chips when the question is asked again in other words. No options: no group.
 */
function chipGroup(options: readonly ChipOption[], describedBy: string, attrs: { readonly class: string; readonly aria: Readonly<Record<string, string>> }): { readonly group: HTMLElement | undefined; readonly handle: QuestionHandle } {
  let spent = false;
  const chips = options.map((o) => {
    // F11 (DCTA-132, §F.2 15j): a label with " — " is the deck's two-line chip — the words before it over a <small>
    // after it, as a card row's title sits over its sub-line; one button, named by its whole label. Others: pills.
    // DCTA-143: a marked chip (a payee never paid, §F.2 none yet) is two-line too: the label over the marker, named
    // by both ("Daniel Ng ···6607 — New payee · never paid").
    const cut = o.label.indexOf(" — ");
    const mark = o.mark === undefined || o.mark === "" ? undefined : o.mark;
    const lines = cut < 0 ? [o.label] : [o.label.slice(0, cut), el("small", {}, [o.label.slice(cut + 3)])];
    const chip =
      cut < 0 && mark === undefined
        ? el("button", { class: "raw opt", type: "button", aria: { pressed: "false" } }, [o.label])
        : el("button", { class: "raw opt two", type: "button", aria: { pressed: "false", label: mark === undefined ? o.label : `${o.label} — ${mark}` } }, [
            ...lines,
            ...(mark === undefined ? [] : [el("small", { class: "mark" }, [mark])]),
          ]);
    chip.addEventListener("click", () => {
      if (!spent) o.onSelect();
    });
    return chip;
  });
  const group = chips.length === 0 ? undefined : el("div", { class: attrs.class, role: "group", aria: attrs.aria }, chips);
  return {
    group,
    handle: {
      spend: (pressed?: number) => {
        spent = true;
        chips.forEach((chip, i) => {
          chip.setAttribute("aria-pressed", i === pressed ? "true" : "false");
          chip.setAttribute("aria-disabled", "true");
        });
        group?.setAttribute("data-spent", "true");
      },
      withdraw: () => {
        spent = true;
        group?.remove();
      },
      holdsFocus: () => group !== undefined && typeof document !== "undefined" && group.contains(document.activeElement),
      focus: () => {
        const chip = chips[0];
        // DCTA-189: a withdrawn group's chips are gone from the page — nothing to focus.
        if (chip === undefined || !chip.isConnected) return false;
        chip.focus({ preventScroll: true });
        return true;
      },
      describedBy,
    },
  };
}

export class ChatView {
  readonly parts: readonly HTMLElement[];
  readonly body: HTMLElement;
  readonly topbar: HTMLElement;
  readonly dock: HTMLElement;
  readonly composer: Composer;
  #greeting: HTMLElement | undefined;
  #interim: HTMLElement | undefined;
  /** DCTA-181: listening is over and the app waits for the engine's last words — they are no longer a control. */
  #interimLocked = false;
  /** Claude's ruling 2 (13 Oct): the voice-clip line, under the composer. */
  readonly #cnote: HTMLElement;

  readonly #onEdit: () => void;

  constructor(handlers: { readonly onBack: () => void; readonly onEdit: () => void } & ComposerHandlers) {
    this.#onEdit = handlers.onEdit;
    const back = el("button", { class: "raw icobtn", type: "button", aria: { label: "Back to Home" } }, [icon("i-chev-l")]);
    back.addEventListener("click", handlers.onBack);
    this.topbar = el("div", { class: "topbar" }, [
      back,
      el("span", { class: "t" }, ["Assistant"]),
      el("span", { class: "topbar-pad", aria: { hidden: "true" } }),
    ]);
    this.body = el("div", { class: "body", role: "log", aria: { label: "Conversation" } });
    this.composer = new Composer(handlers);
    // §D.5: the dock floats over the stream on glass; the stream is padded by its measured height.
    this.#cnote = el("p", { class: "cnote", hidden: true });
    this.dock = el("div", { class: "dock over" }, [this.composer.element, this.#cnote]);
    this.parts = [this.topbar, this.body, this.dock];
    if (typeof ResizeObserver !== "undefined") new ResizeObserver(() => this.#padForDock()).observe(this.dock);
  }

  /**
   * Claude's ruling 2 (13 Oct): one small line under the composer — the voice-clip line, shown once a session, said once
   * in the composer's polite region; `null` takes it away. Text only (it never carries markup).
   */
  consentNote(text: string | null): void {
    this.#cnote.textContent = text ?? "";
    this.#cnote.hidden = text === null;
    if (text !== null) this.composer.status(text);
  }

  /**
   * The dock's height → `--dock-pad` on the column that holds it (shared.js `syncDocks`), so the
   * newest line and every focused control stop above the glass (WCAG 2.4.11). A stream that was
   * at its newest line stays there as the dock grows (a field growing to five lines). The
   * column clears it when another screen mounts (main.ts `mount`).
   */
  #padForDock(): void {
    const host = this.dock.parentElement;
    const h = Math.round(this.dock.getBoundingClientRect().height);
    if (host === null || !this.dock.isConnected || h === 0) return;
    const b = this.body;
    const atEnd = b.scrollHeight - b.scrollTop - b.clientHeight <= 4;
    host.style.setProperty("--dock-pad", `${String(h)}px`);
    if (atEnd) this.scrollToNewest();
  }

  greet(name: string): void {
    this.#greeting = this.#turn(`Hi ${name}. What would you like to do?`);
    this.#greeting.classList.add("greet");
    this.body.appendChild(this.#greeting);
    this.body.appendChild(el("div", { class: "stream-end", aria: { hidden: "true" } }));
  }

  /** The greeting buys ~60 px for the bank card once the first words are final. */
  collapseGreeting(): void {
    this.#greeting?.classList.add("collapsed");
  }

  /**
   * QA-04: the conversation a reload kept (lib/stream-snapshot.ts `rebuildStream`), under the greeting — collapsed, as it
   * was once words were in — and before the stream's end. Drawn while the view is out of the page, so the log's live region
   * reads none of it out again.
   */
  restore(past: DocumentFragment): void {
    if (past.childNodes.length === 0) return;
    this.collapseGreeting();
    this.body.insertBefore(past, this.body.querySelector(".stream-end"));
  }

  /**
   * The customer's words while they speak (DCTA-141): ink-2, one bubble that grows, and a button — a tap stops the
   * automatic send and opens the words in the field. Its name is what it shows, the words and "Tap to edit" (WCAG
   * 2.5.3); nothing announces it while they speak — the log is quiet then (`quiet`) — and focused, it reads them back.
   */
  interim(text: string): void {
    if (this.#interim === undefined) {
      const words = el("span", { class: "words" });
      const button = el("button", { class: "raw heard", type: "button" }, [words, el("span", { class: "hint" }, ["Tap to edit"])]);
      button.addEventListener("click", () => {
        if (!this.#interimLocked) this.#onEdit();
      });
      this.#interim = el("div", { class: "me interim enter" }, [button]);
      this.append(this.#interim);
      if (this.#interimLocked) this.#lockBubble();
    }
    const words = this.#interim.querySelector(".words");
    if (words !== null) words.textContent = text;
    this.scrollToNewest();
  }

  /**
   * DCTA-181: listening is over (Done, the release, the silence) and the app waits for the engine's last words. The words
   * so far stay and grow, but "Tap to edit" would do nothing now: the bubble is no longer a control. A new listening's
   * bubble is one again (`dropInterim`).
   */
  lockInterim(): void {
    this.#interimLocked = true;
    this.#lockBubble();
  }

  #lockBubble(): void {
    const button = this.#interim?.querySelector(".heard");
    if (button === null || button === undefined) return;
    button.setAttribute("aria-disabled", "true");
    button.querySelector(".hint")?.remove();
  }

  /** While the customer speaks the log says nothing (a screen reader's voice would reach the microphone); after, it speaks again. */
  quiet(on: boolean): void {
    if (on) this.body.setAttribute("aria-live", "off");
    else this.body.removeAttribute("aria-live");
  }

  dropInterim(): void {
    this.#interim?.remove();
    this.#interim = undefined;
    this.#interimLocked = false;
  }

  /** DCTA-181: is `node` still the newest thing in the conversation (the words being spoken aside)? */
  isNewest(node: HTMLElement): boolean {
    const end = this.body.querySelector(".stream-end");
    let last = end === null ? this.body.lastElementChild : end.previousElementSibling;
    while (last !== null && last.classList.contains("interim")) last = last.previousElementSibling;
    return last === node;
  }

  /**
   * DCTA-162: a quiet line in the log — not the assistant's turn, no Bank tag (the browser's engine took over a listening:
   * "say so quietly", Claude's brief). Muted, centred, text only.
   */
  note(text: string): HTMLElement {
    const line = el("p", { class: "chat-note" }, [text]);
    this.append(line);
    return line;
  }

  /**
   * The customer's finalised words — a new node, so the log reads it once. DCTA-162: a voice turn (`said`) carries the
   * "You said" label (§F.2 11q's word for the customer's own reading) above the words as the engine heard them, the figures
   * in bold — "fifty" or "fifteen" is seen before the bank answers (P4). Text nodes only.
   */
  me(text: string, how: { readonly said?: boolean } = {}): HTMLElement {
    this.dropInterim();
    const said = how.said === true;
    const words = el("p", {}, said ? figured(text) : [text]);
    const bubble = el("div", { class: said ? "me said enter" : "me enter" }, said ? [el("small", { class: "said-lbl" }, ["You said"]), words] : [words]);
    this.append(bubble);
    return bubble;
  }

  /** The assistant's typing dots; replaced by its line when the answer is in. */
  typing(): HTMLElement {
    const turn = el("div", { class: "turn enter" }, [
      el("span", { class: "ai-mark", aria: { hidden: "true" } }),
      el("div", {}, [
        el("span", { class: "who" }, ["Assistant"]),
        el("span", { class: "typing", role: "status", aria: { label: "Assistant is replying" } }, [el("i"), el("i"), el("i")]),
      ]),
    ]);
    this.append(turn);
    return turn;
  }

  /**
   * DCTA-111 — the wait, when the bank may take 3–15 s: the dots, Cancel and one status line,
   * ALL built now and in the layout from the start (Cancel invisible, the line empty but its slot
   * as tall as its longest line — app.css 41), so a later phase reveals or rewords and nothing
   * moves. The line is the row's ONE live region (DCTA-120, review P2-5): the dots are `aria-hidden`
   * decoration, and while quiet the line holds a visually hidden "Assistant is replying". The row is replaced
   * in place by whatever answers (`assistant`, `question` and the card's ready line take it as
   * `replacing`).
   */
  working(onCancel: () => void): WorkingRow {
    const cancel = el("button", { class: "raw wait-x", type: "button", aria: { label: "Cancel this request" } }, ["Cancel"]);
    let cancelled = false;
    cancel.addEventListener("click", () => {
      if (cancelled) return;
      cancelled = true;
      onCancel();
    });
    const line = el("p", { class: "wait", role: "status", aria: { live: "polite", atomic: "true" } });
    // DCTA-217: the line's slot is as tall as the longest thing the wait will say at this width and type size — the 10 s
    // and 25 s lines ride on it as invisible generated text (app.css 41), never as DOM text, so a reword never grows the
    // row and the live region stays the only thing announced.
    const slot = el("div", { class: "wait-slot", data: { still: WAIT_LINES.still, late: WAIT_LINES.late } }, [line]);
    const turn = el("div", { class: "turn work enter", data: { phase: "quiet" } }, [
      el("span", { class: "ai-mark", aria: { hidden: "true" } }),
      el("div", {}, [
        el("span", { class: "who" }, ["Assistant"]),
        // DCTA-216: `.wait-row`, not `.wl` — vault.css §14 gave `.wl` to the first-run welcome's layer (a full-screen dim).
        el("div", { class: "wait-row" }, [el("span", { class: "typing", aria: { hidden: "true" } }, [el("i"), el("i"), el("i")]), cancel]),
        slot,
      ]),
    ]);
    this.append(turn);
    this.scrollToNewest();
    return {
      element: turn,
      phase: (phase) => {
        turn.dataset["phase"] = phase;
        if (phase === "quiet") line.replaceChildren(el("span", { class: "vis-hidden" }, [QUIET]));
        else line.textContent = WAIT_LINES[phase];
      },
    };
  }

  /**
   * The assistant's line, in place of `replacing` (the dots or the waiting row) when it is still on
   * screen. `judgeCode` (judge mode only, DCTA-120): the reason code under the line, inside a `.judge`
   * root — never customer copy.
   */
  assistant(text: string, replacing?: HTMLElement, judgeCode?: string): HTMLElement {
    return this.message([text], { replacing, judge: judgeCode });
  }

  /**
   * DCTA-124 (Umakhan's ruling, 5 Oct): whatever is not the card to sign — a policy or an injection
   * refusal, "nothing was sent", the freeze, a protective action's result — is the assistant's message:
   * the bank's sentences verbatim (screen = record), no Bank tag, no "Not sent" pill. `pairs`: the freeze's
   * two readings side by side; `judge`: a rule id or reason code for judge mode only, under a `.judge` root.
   * `judgeBlock` (DCTA-188): judge mode's own block under it — a freeze's rule, re-check and both passes — itself a
   * `.judge` root (the view in ui/judge.ts), drawn last.
   */
  message(
    lines: readonly string[],
    opts: {
      readonly replacing?: HTMLElement | undefined;
      readonly judge?: string | undefined;
      readonly pairs?: readonly { readonly said: string; readonly about: string }[] | undefined;
      readonly judgeBlock?: HTMLElement | undefined;
    } = {},
  ): HTMLElement {
    const turn = el("div", { class: "turn enter" }, [
      el("span", { class: "ai-mark", aria: { hidden: "true" } }),
      el("div", {}, [
        el("span", { class: "who" }, ["Assistant"]),
        ...lines.map((line) => el("p", {}, [line])),
        ...(opts.pairs ?? []).map((pair) =>
          el("div", { class: "pair" }, [
            el("div", {}, [el("small", {}, ["You said"]), el("b", {}, [pair.said])]),
            el("div", {}, [el("small", {}, ["Was about to send"]), el("b", {}, withMoney(pair.about))]),
          ]),
        ),
        ...(opts.judge === undefined || opts.judge === "" ? [] : [el("p", { class: "judge jcode" }, [el("span", { class: "mono" }, [opts.judge])])]),
        ...(opts.judgeBlock === undefined ? [] : [opts.judgeBlock]),
      ]),
    ]);
    const replacing = opts.replacing;
    if (replacing !== undefined && replacing.isConnected) replacing.replaceWith(turn);
    else this.append(turn);
    this.scrollToNewest();
    return turn;
  }

  /**
   * DCTA-188: judge mode's note on a turn already drawn — the lead of a card or a question whose response says the silent
   * re-check cleared it. The note is its own `.judge` root (ui/judge.ts `clearedJudgeView`); the turn's words stay as they are.
   */
  judgeNote(turn: HTMLElement, note: HTMLElement): void {
    const inner = turn.querySelector(":scope > div") ?? turn;
    inner.append(note);
  }

  /** DCTA-188: the note on the newest assistant turn — a card's lead line (a card is no turn) or a question. */
  judgeNoteNewest(note: HTMLElement): void {
    const newest = this.#newestAssistant();
    if (newest !== undefined) this.judgeNote(newest, note);
  }

  #newestAssistant(): HTMLElement | undefined {
    return [...this.body.querySelectorAll<HTMLElement>(".turn")].filter((t) => t.querySelector(":scope > div > .who") !== null).at(-1);
  }

  /**
   * DCTA-188 Change 4 (beat 5): the bank's label that this answer's draft was altered on purpose — judge mode's faulty-drafter
   * demo — as the FIRST line of the answer's turn, above the bank's sentence, so the frame always shows it was staged. Judge
   * mode's register (the outlined Judge tag; a `.judge` root: the label names the machinery, which customer copy never does).
   */
  demoLabel(text: string): void {
    const turn = this.#newestAssistant();
    const who = turn?.querySelector(":scope > div > .who");
    who?.after(judgeLabel(text));
  }

  /** DCTA-188 Change 4: an assistant turn whose only line is judge mode's (a demo that caught nothing), in place of `replacing`. */
  judgeLine(text: string, replacing?: HTMLElement): HTMLElement {
    const turn = el("div", { class: "turn enter" }, [el("span", { class: "ai-mark", aria: { hidden: "true" } }), el("div", {}, [el("span", { class: "who" }, ["Assistant"]), judgeLabel(text)])]);
    if (replacing !== undefined && replacing.isConnected) replacing.replaceWith(turn);
    else this.append(turn);
    this.scrollToNewest();
    return turn;
  }

  /**
   * The ways on under an assistant's message (DCTA-124): one chip each, on the assistant's surface (§9.3
   * no. 30: an offer that is not a signature lives here). An offer (`once`) goes one time — the group is
   * spent; Talk to us can be used again. A new request quiets the group (`retireOffers`). `words` (DCTA-154, §F.2 21s):
   * chips that are used again but that the customer's words may still answer, as an offer's are (a cap refusal's "See
   * my limits" and "Talk to us": a "yes" under two of them is asked about, 16h). `standing` (DCTA-189, QA DW-2): ways on
   * a read-only turn leaves live (`retireOffers({ read: true })`) — the identity check's "Update my details".
   */
  actions(
    acts: readonly { readonly label: string; readonly small?: string; readonly onSelect: () => void; readonly once?: boolean; readonly name?: string; readonly asks?: OfferTarget }[],
    opts: { readonly words?: boolean; readonly standing?: boolean } = {},
  ): void {
    if (acts.length === 0) return;
    const group = el("div", { class: "opts enter", role: "group", aria: { label: "Options" } });
    group.dataset["offer"] = "true";
    // DCTA-226: the one offer of the bank's these chips answer (words that ask go there) — none when they answer two.
    const targets = acts.flatMap((a) => (a.asks === undefined ? [] : [a.asks]));
    const [target] = targets;
    if (target !== undefined && targets.every((t) => t.draftId === target.draftId && t.questionId === target.questionId)) offerTargets.set(group, target);
    if (opts.words === true) group.dataset["words"] = "true";
    if (opts.standing === true) group.dataset["standing"] = "true";
    for (const a of acts) {
      const chip =
        a.small === undefined
          ? el("button", { class: "raw opt", type: "button", aria: { pressed: "false" } }, [a.label])
          : el("button", { class: "raw opt two", type: "button", aria: { pressed: "false" } }, [a.label, el("small", {}, [a.small])]);
      // DCTA-176 (24d): a two-line chip whose lines would run together when read ("Ask again“Send…”") is named with a pause.
      if (a.name !== undefined) chip.setAttribute("aria-label", a.name);
      chip.dataset["label"] = a.label;
      if (a.once === true) {
        chip.dataset["once"] = "true";
        group.dataset["once"] = "true";
      }
      chip.addEventListener("click", () => {
        if (group.dataset["spent"] === "true" || group.dataset["held"] === "true") return;
        if (a.once === true) {
          group.dataset["spent"] = "true";
          chip.setAttribute("aria-pressed", "true");
          for (const c of group.querySelectorAll("button")) c.setAttribute("aria-disabled", "true");
        }
        a.onSelect();
      });
      group.append(chip);
    }
    this.append(group);
    this.scrollToNewest();
  }

  /**
   * 04: the resolver's question as the assistant's turn, the server's options as
   * chips under it. A chip is used once: `spend(i)` keeps the chosen one pressed
   * and makes the group inert (the transcript still reads as a conversation);
   * `withdraw()` removes the chips when the question is asked again in other
   * words, so there is never more than one open group on screen. `lead` (DCTA-136, F19): the bank's
   * sentence when it refused an answer and kept the question open — said first, verbatim, as a plain line;
   * the question follows it, asked again, with its chips. `still` (DCTA-221): the question was on the card the customer just
   * answered from and is asked again — `STILL_OPEN` above its sentence.
   */
  question(text: string, options: readonly ChipOption[], replacing?: HTMLElement, lead?: string, still = false): QuestionHandle {
    const describedBy = nextQuestionId();
    const turn = el("div", { class: "turn enter" }, [
      el("span", { class: "ai-mark", aria: { hidden: "true" } }),
      el("div", {}, [
        el("span", { class: "who" }, ["Assistant"]),
        ...(lead === undefined || lead === "" ? [] : [el("p", {}, [lead])]),
        ...stillMark(still),
        el("p", { class: "q", id: describedBy }, [text]),
      ]),
    ]);
    if (replacing !== undefined && replacing.isConnected) replacing.replaceWith(turn);
    else this.append(turn);
    const asked = chipGroup(options, describedBy, { class: "opts enter", aria: { label: "Options" } });
    if (asked.group !== undefined) this.append(asked.group);
    this.scrollToNewest();
    return asked.handle;
  }

  /**
   * DCTA-189 (QA DW-4, demo beat 4): the questions the bank asked in ONE turn ("Two Johns are saved. Which one?" · "And
   * fifty — fifty dollars, or fifty thousand?"), on one card — one assistant turn, each sentence followed by its own chips,
   * each group named by its sentence (`aria-labelledby`: a screen reader hears which question a chip answers). Every group
   * is answered, spent or withdrawn on its own (its handle). One question is drawn exactly as `question` draws it.
   */
  questions(
    items: readonly { readonly text: string; readonly options: readonly ChipOption[]; readonly still?: boolean }[],
    replacing?: HTMLElement,
    lead?: string,
  ): QuestionHandle[] {
    const [only] = items;
    if (only === undefined) return [];
    if (items.length === 1) return [this.question(only.text, only.options, replacing, lead, only.still === true)];
    const asked = items.map((item) => {
      const id = nextQuestionId();
      return { mark: stillMark(item.still === true), sentence: el("p", { class: "q", id }, [item.text]), ...chipGroup(item.options, id, { class: "opts", aria: { labelledby: id } }) };
    });
    const turn = el("div", { class: "turn qs enter" }, [
      el("span", { class: "ai-mark", aria: { hidden: "true" } }),
      el("div", {}, [
        el("span", { class: "who" }, ["Assistant"]),
        ...(lead === undefined || lead === "" ? [] : [el("p", {}, [lead])]),
        ...asked.flatMap((a) => [...a.mark, a.sentence, ...(a.group === undefined ? [] : [a.group])]),
      ]),
    ]);
    if (replacing !== undefined && replacing.isConnected) replacing.replaceWith(turn);
    else this.append(turn);
    this.scrollToNewest();
    return asked.map((a) => a.handle);
  }

  /**
   * 10 bill due: the assistant's offer under a read card — ONE two-line chip on the
   * assistant's surface (never a button on the bank's). It starts a draft that takes
   * the full path; used once.
   */
  offer(label: string, small: string, onSelect: () => void): void {
    this.actions([{ label, small, once: true, onSelect }]);
  }

  /**
   * DCTA-154 F27 (b) (backend DCTA-157 B38; Claude's ruling): the bank refused a payment for a Yes it carries for that
   * payee — drawn as the card's paused end (§F.2 21f–21h): the pill "Paused", the bank's words as the paused notice, and
   * the three safe ways on. The card's frame, never the card to sign: no Bank tag, no bundle, no rows, no Sign.
   */
  pausedStop(notice: { readonly lead: string; readonly rest: string }, h: { readonly onTalk: () => void; readonly onLock: () => void }, replacing?: HTMLElement): HTMLElement {
    const talk = el("button", { class: "raw btn", type: "button" }, ["Talk to us"]);
    talk.addEventListener("click", h.onTalk);
    const lock = el("button", { class: "raw btn ghost", type: "button" }, ["Lock my money"]);
    lock.addEventListener("click", h.onLock);
    const call = el("a", { class: "raw btn link", href: "tel:1799" }, ["Call 1799 · ScamShield"]);
    const end = el("div", { class: "bank paused-stop enter", role: "group", aria: { label: "Paused" } }, [
      el("div", { class: "bh" }, [el("span", { class: "status" }, ["Paused"])]),
      el("div", { class: "bf" }, [
        el("div", { class: "end" }, [
          el("div", { class: "notice end" }, [icon("i-pause"), el("div", {}, [...(notice.lead === "" ? [] : [el("b", {}, [notice.lead])]), notice.rest])]),
          el("div", { class: "acts" }, [talk, lock, call]),
        ]),
      ]),
    ]);
    if (replacing !== undefined && replacing.isConnected) replacing.replaceWith(end);
    else this.append(end);
    this.scrollToNewest();
    return end;
  }

  /**
   * The newest offer still open (a group with chips used once — or, DCTA-154, a group words may answer), or none — words
   * can answer it (§F.2 16d, 16h).
   */
  openOffer(): OpenOffer | undefined {
    const group = [...this.body.querySelectorAll<HTMLElement>(".opts[data-offer]:is([data-once],[data-words]):not([data-spent])")].at(-1);
    if (group === undefined) return undefined;
    const chips = [...group.querySelectorAll<HTMLButtonElement>(group.dataset["words"] === "true" ? "button.opt" : "button.opt[data-once]")];
    const asks = offerTargets.get(group);
    return {
      labels: chips.map((c) => c.dataset["label"] ?? ""),
      ...(asks === undefined ? {} : { asks }),
      reasked: group.dataset["reasked"] === "true",
      reask: () => {
        group.dataset["reasked"] = "true";
      },
      pick: (index) => chips[index]?.click(),
      hold: (on) => {
        if (on) group.dataset["held"] = "true";
        else delete group.dataset["held"];
        if (group.dataset["spent"] === "true") return;
        for (const chip of group.querySelectorAll("button")) {
          if (on) chip.setAttribute("aria-disabled", "true");
          else chip.removeAttribute("aria-disabled");
        }
      },
    };
  }

  /**
   * A new request: an offer still open goes quiet, so there is never more than one open group. DCTA-189 (QA DW-2): a
   * read-only turn (`read`: "What's my balance?") leaves a standing group live — the identity check's way on is still true.
   */
  retireOffers(how: { readonly read?: boolean } = {}): void {
    for (const group of this.body.querySelectorAll<HTMLElement>(how.read === true ? ".opts[data-offer]:not([data-spent]):not([data-standing])" : ".opts[data-offer]:not([data-spent])")) {
      group.dataset["spent"] = "true";
      for (const chip of group.querySelectorAll("button")) chip.setAttribute("aria-disabled", "true");
    }
  }

  append(node: HTMLElement): void {
    const end = this.body.querySelector(".stream-end");
    this.body.insertBefore(node, end);
    // Ruling P2-4: the keyboard stays up through the conversation and drops for the bank's card.
    if (node.classList.contains("bank")) this.composer.dropKeyboard();
  }

  scrollToNewest(): void {
    this.body.scrollTop = this.body.scrollHeight;
  }

  #turn(text: string): HTMLElement {
    return el("div", { class: "turn" }, [
      el("span", { class: "ai-mark", aria: { hidden: "true" } }),
      el("div", {}, [el("span", { class: "who" }, ["Assistant"]), el("p", {}, [text])]),
    ]);
  }
}

/** DCTA-162: a figure as an engine writes it — digits (grouped, with cents) or a number word ("fifty-five", "hundred"). */
const UNITS = "zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen";
const TENS = "twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety";
const FIGURE = new RegExp(`\\d+(?:,\\d{3})*(?:\\.\\d+)?|\\b(?:(?:${TENS})(?:-(?:one|two|three|four|five|six|seven|eight|nine))?|${UNITS}|hundred|thousand|million|billion)\\b`, "gi");

/** The heard words with each figure in a <b> — text nodes and elements made here, never markup from the words. */
function figured(text: string): (string | HTMLElement)[] {
  const parts: (string | HTMLElement)[] = [];
  let at = 0;
  for (const m of text.matchAll(FIGURE)) {
    const start = m.index;
    if (start > at) parts.push(text.slice(at, start));
    parts.push(el("b", {}, [m[0]]));
    at = start + m[0].length;
  }
  if (at < text.length) parts.push(text.slice(at));
  return parts;
}

/** DCTA-221: the still-open mark above a question asked again — one small line of text, or nothing. */
function stillMark(still: boolean): HTMLElement[] {
  return still ? [el("p", { class: "q-still" }, [STILL_OPEN])] : [];
}

/** DCTA-188: one line in judge mode's register inside the chat — the outlined Judge tag, then the words, as text. */
function judgeLabel(text: string): HTMLElement {
  return el("p", { class: "judge jlabel" }, [el("span", { class: "judgetag" }, ["Judge"]), " ", el("span", { class: "lbl" }, [text])]);
}
